import { useSyncExternalStore } from 'react'
import { readStorage, writeStorage } from './storage'

export type Theme = 'light' | 'dark'

const storageKey = 'c-sheet:theme'
const listeners = new Set<() => void>()

function systemQuery(): MediaQueryList {
  return window.matchMedia('(prefers-color-scheme: dark)')
}

function storedTheme(): Theme | null {
  const value = readStorage(storageKey)
  return value === 'light' || value === 'dark' ? value : null
}

/** The chosen theme, or the operating system's setting until one is chosen. */
function currentTheme(): Theme {
  return storedTheme() ?? (systemQuery().matches ? 'dark' : 'light')
}

function applyTheme(): void {
  document.documentElement.classList.toggle('dark', currentTheme() === 'dark')
  for (const listener of listeners) listener()
}

// Applies the theme and follows changes of the operating system's setting while none is chosen.
export function initTheme(): void {
  applyTheme()
  systemQuery().addEventListener('change', applyTheme)
}

export function setTheme(theme: Theme): void {
  writeStorage(storageKey, theme)
  document.documentElement.classList.toggle('dark', theme === 'dark')
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, () =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  )
}
